/**
 * Postprocessing pipeline using pmndrs/postprocessing.
 * Manages EffectComposer with all screen-space effects.
 *
 * Pass structure (per EFFECTS.md merging rules):
 * 1. RenderPass — renders the scene
 * 2. EffectPass(SMAA) — alone, conflicts with convolution effects
 * 3. EffectPass(Bloom) — alone, is convolution
 * 4. EffectPass(Vignette, ChromaticAberration, Noise, Glitch, Scanline, ToneMapping) — merged
 *
 * All effects read enabled/params from usePostprocessingStore.
 * Effects use BlendFunction.SKIP to disable at zero cost.
 * Passes use .enabled property for pass-level disable.
 */

import { useEffect, useRef } from 'react';
import { useThree, useFrame } from '@react-three/fiber';
import {
  EffectComposer,
  RenderPass,
  EffectPass,
  SMAAEffect,
  SMAAPreset,
  BloomEffect,
  BlendFunction,
  VignetteEffect,
  ChromaticAberrationEffect,
  NoiseEffect,
  ToneMappingEffect,
  GlitchEffect,
  ScanlineEffect,
} from 'postprocessing';
import { HalfFloatType, Vector2, NoToneMapping } from 'three';
import { usePostprocessingStore } from '../../stores/postprocessingStore';
import type { ToneMappingModeValue } from '../../stores/postprocessingStore';

/** Map our store's ToneMappingMode to postprocessing's ToneMappingMode enum values */
function toPostprocessingToneMappingMode(mode: ToneMappingModeValue): number {
  // postprocessing ToneMappingMode enum:
  // LINEAR = 0, REINHARD = 1, UNCHARTED2 = 2, ACES_FILMIC = 4
  // Our store uses the same values, so direct pass-through
  return mode;
}

/** Store the original BlendFunction for each effect so we can restore after SKIP */
interface EffectRefs {
  smaa: SMAAEffect;
  bloom: BloomEffect;
  vignette: VignetteEffect;
  chromaticAberration: ChromaticAberrationEffect;
  noise: NoiseEffect;
  toneMapping: ToneMappingEffect;
  glitch: GlitchEffect;
  scanline: ScanlineEffect;
  smaaPass: EffectPass;
  bloomPass: EffectPass;
  mergedPass: EffectPass;
  // Original blend functions for restoring after SKIP
  bloomBlend: number;
  vignetteBlend: number;
  chromaticAberrationBlend: number;
  noiseBlend: number;
  toneMappingBlend: number;
  glitchBlend: number;
  scanlineBlend: number;
}

export function PostprocessingPipeline() {
  const gl = useThree((s) => s.gl);
  const scene = useThree((s) => s.scene);
  const camera = useThree((s) => s.camera);
  const size = useThree((s) => s.size);
  const composerRef = useRef<EffectComposer | null>(null);
  const effectsRef = useRef<EffectRefs | null>(null);

  // Initialize composer and effects once
  useEffect(() => {
    // Disable Three.js built-in tone mapping — postprocessing handles it
    gl.toneMapping = NoToneMapping;

    const composer = new EffectComposer(gl, {
      frameBufferType: HalfFloatType,
    });

    // Read initial settings
    const s = usePostprocessingStore.getState().settings;

    // Create effects
    const smaaEffect = new SMAAEffect({ preset: SMAAPreset.HIGH });

    const bloomEffect = new BloomEffect({
      blendFunction: BlendFunction.ADD,
      luminanceThreshold: s.bloom.luminanceThreshold,
      luminanceSmoothing: s.bloom.luminanceSmoothing,
      intensity: s.bloom.intensity,
      mipmapBlur: s.bloom.mipmapBlur,
      radius: s.bloom.radius,
    });

    const vignetteEffect = new VignetteEffect({
      darkness: s.vignette.darkness,
      offset: s.vignette.offset,
    });

    const chromaticAberrationEffect = new ChromaticAberrationEffect({
      offset: new Vector2(s.chromaticAberration.offsetX, s.chromaticAberration.offsetY),
      radialModulation: s.chromaticAberration.radialModulation,
      modulationOffset: s.chromaticAberration.modulationOffset,
    });

    const noiseEffect = new NoiseEffect({
      blendFunction: s.noise.premultiply ? BlendFunction.MULTIPLY : BlendFunction.SCREEN,
    });
    noiseEffect.blendMode.opacity.value = s.noise.opacity;

    const toneMappingEffect = new ToneMappingEffect({
      mode: toPostprocessingToneMappingMode(s.toneMapping.mode),
    });

    const glitchEffect = new GlitchEffect({
      delay: new Vector2(s.glitch.delayMin, s.glitch.delayMax),
      duration: new Vector2(s.glitch.durationMin, s.glitch.durationMax),
      strength: new Vector2(s.glitch.strengthMin, s.glitch.strengthMax),
    });

    const scanlineEffect = new ScanlineEffect({
      density: s.scanline.density,
    });
    scanlineEffect.blendMode.opacity.value = s.scanline.opacity;

    // Store original blend functions before any SKIP overrides
    const bloomBlend = bloomEffect.blendMode.blendFunction;
    const vignetteBlend = vignetteEffect.blendMode.blendFunction;
    const chromaticAberrationBlend = chromaticAberrationEffect.blendMode.blendFunction;
    const noiseBlend = noiseEffect.blendMode.blendFunction;
    const toneMappingBlend = toneMappingEffect.blendMode.blendFunction;
    const glitchBlend = glitchEffect.blendMode.blendFunction;
    const scanlineBlend = scanlineEffect.blendMode.blendFunction;

    // Apply initial enabled state via BlendFunction.SKIP
    if (!s.bloom.enabled) bloomEffect.blendMode.blendFunction = BlendFunction.SKIP;
    if (!s.vignette.enabled) vignetteEffect.blendMode.blendFunction = BlendFunction.SKIP;
    if (!s.chromaticAberration.enabled) chromaticAberrationEffect.blendMode.blendFunction = BlendFunction.SKIP;
    if (!s.noise.enabled) noiseEffect.blendMode.blendFunction = BlendFunction.SKIP;
    if (!s.toneMapping.enabled) toneMappingEffect.blendMode.blendFunction = BlendFunction.SKIP;
    if (!s.glitch.enabled) glitchEffect.blendMode.blendFunction = BlendFunction.SKIP;
    if (!s.scanline.enabled) scanlineEffect.blendMode.blendFunction = BlendFunction.SKIP;

    // Build passes
    const renderPass = new RenderPass(scene, camera);
    const smaaPass = new EffectPass(camera, smaaEffect);
    const bloomPass = new EffectPass(camera, bloomEffect);
    const mergedPass = new EffectPass(
      camera,
      vignetteEffect,
      chromaticAberrationEffect,
      noiseEffect,
      glitchEffect,
      scanlineEffect,
      toneMappingEffect,
    );

    // Apply initial pass-level enabled state
    smaaPass.enabled = s.smaa.enabled;

    composer.addPass(renderPass);
    composer.addPass(smaaPass);
    composer.addPass(bloomPass);
    composer.addPass(mergedPass);

    composerRef.current = composer;
    effectsRef.current = {
      smaa: smaaEffect,
      bloom: bloomEffect,
      vignette: vignetteEffect,
      chromaticAberration: chromaticAberrationEffect,
      noise: noiseEffect,
      toneMapping: toneMappingEffect,
      glitch: glitchEffect,
      scanline: scanlineEffect,
      smaaPass,
      bloomPass,
      mergedPass,
      bloomBlend,
      vignetteBlend,
      chromaticAberrationBlend,
      noiseBlend,
      toneMappingBlend,
      glitchBlend,
      scanlineBlend,
    };

    return () => {
      composer.dispose();
      composerRef.current = null;
      effectsRef.current = null;
    };
  }, [gl, scene, camera]);

  // Handle resize — depend on dimension values, not object reference.
  // R3F may recreate the size object without dimensions changing; comparing
  // width/height avoids unnecessary framebuffer reallocation.
  useEffect(() => {
    composerRef.current?.setSize(size.width, size.height);
  }, [size.width, size.height]);

  // Sync settings from store to effects every frame
  useFrame(() => {
    const fx = effectsRef.current;
    if (!fx) return;

    const s = usePostprocessingStore.getState().settings;

    // Bloom: use BlendFunction.SKIP to disable, restore original to enable
    fx.bloom.blendMode.blendFunction = s.bloom.enabled ? fx.bloomBlend : BlendFunction.SKIP;
    if (s.bloom.enabled) {
      fx.bloom.intensity = s.bloom.intensity;
      fx.bloom.luminanceMaterial.threshold = s.bloom.luminanceThreshold;
      fx.bloom.luminanceMaterial.smoothing = s.bloom.luminanceSmoothing;
      (fx.bloom as unknown as { mipmapBlurPass: { radius: number } }).mipmapBlurPass.radius = s.bloom.radius;
    }

    // Vignette
    fx.vignette.blendMode.blendFunction = s.vignette.enabled ? fx.vignetteBlend : BlendFunction.SKIP;
    if (s.vignette.enabled) {
      (fx.vignette as unknown as { darkness: number }).darkness = s.vignette.darkness;
      (fx.vignette as unknown as { offset: number }).offset = s.vignette.offset;
    }

    // ChromaticAberration
    fx.chromaticAberration.blendMode.blendFunction = s.chromaticAberration.enabled ? fx.chromaticAberrationBlend : BlendFunction.SKIP;
    if (s.chromaticAberration.enabled) {
      fx.chromaticAberration.offset.set(s.chromaticAberration.offsetX, s.chromaticAberration.offsetY);
    }

    // Noise
    fx.noise.blendMode.blendFunction = s.noise.enabled ? fx.noiseBlend : BlendFunction.SKIP;
    if (s.noise.enabled) {
      fx.noise.blendMode.opacity.value = s.noise.opacity;
    }

    // ToneMapping
    fx.toneMapping.blendMode.blendFunction = s.toneMapping.enabled ? fx.toneMappingBlend : BlendFunction.SKIP;

    // ToneMapping: sync mode
    if (s.toneMapping.enabled) {
      (fx.toneMapping as unknown as { mode: number }).mode = toPostprocessingToneMappingMode(s.toneMapping.mode);
    }

    // Glitch: sync delay, duration, strength vectors
    fx.glitch.blendMode.blendFunction = s.glitch.enabled ? fx.glitchBlend : BlendFunction.SKIP;
    if (s.glitch.enabled) {
      fx.glitch.delay.set(s.glitch.delayMin, s.glitch.delayMax);
      fx.glitch.duration.set(s.glitch.durationMin, s.glitch.durationMax);
      fx.glitch.strength.set(s.glitch.strengthMin, s.glitch.strengthMax);
    }

    // Scanline: sync density, opacity, scrollSpeed
    fx.scanline.blendMode.blendFunction = s.scanline.enabled ? fx.scanlineBlend : BlendFunction.SKIP;
    if (s.scanline.enabled) {
      (fx.scanline as unknown as { density: number }).density = s.scanline.density;
      fx.scanline.blendMode.opacity.value = s.scanline.opacity;
      (fx.scanline as unknown as { scrollSpeed: number }).scrollSpeed = s.scanline.scrollSpeed;
    }

    // ChromaticAberration: sync radialModulation and modulationOffset
    if (s.chromaticAberration.enabled) {
      (fx.chromaticAberration as unknown as { radialModulation: boolean }).radialModulation = s.chromaticAberration.radialModulation;
      (fx.chromaticAberration as unknown as { modulationOffset: number }).modulationOffset = s.chromaticAberration.modulationOffset;
    }

    // Noise: sync premultiply (changes blend function)
    if (s.noise.enabled) {
      const targetBlend = s.noise.premultiply ? BlendFunction.MULTIPLY : BlendFunction.SCREEN;
      fx.noise.blendMode.blendFunction = targetBlend;
      // Update stored original so enable/disable restores correctly
      fx.noiseBlend = targetBlend;
    }

    // SMAA: use pass-level .enabled (it's the only effect in its pass)
    fx.smaaPass.enabled = s.smaa.enabled;
  }, -1); // Priority -1: sync settings before rendering

  // Render through composer (with fallback to default render).
  // Priority 1 disables R3F's built-in gl.render(scene, camera). If the
  // EffectComposer isn't ready yet (useEffect hasn't run, or creation failed),
  // we MUST call gl.render ourselves or the screen stays black.
  useFrame(({ gl: renderer, scene: sc, camera: cam }, delta) => {
    if (composerRef.current) {
      composerRef.current.render(delta);
    } else {
      renderer.render(sc, cam);
    }
  }, 1); // Priority 1: render after default R3F scene updates

  return null;
}
